import { fetchAPI } from '@/core/api/client';
import { getOrCreateSeatSessionToken } from '@/lib/seat-map-utils';

const CART_KEY = 'stag-cart';
// Mirrors the backend's CART_HOLD_MINUTES.
const CART_HOLD_MS = 15 * 60 * 1000;

export interface CartSeatItem {
    seat_id: string;
    section: string;
    label: string;
    rowLabel: string;
    seatLabel: string;
    price: number;
}

export function readCart(): any[] {
    try {
        return JSON.parse(localStorage.getItem(CART_KEY) || '[]');
    } catch {
        return [];
    }
}

export function writeCart(cart: any[]) {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
    window.dispatchEvent(new Event('cart-updated'));
}

/**
 * Adds one cart line item per selected seat. Skips seats already present in the cart
 * (deduped by cartItemId). Returns the seats that were newly added, so callers can
 * fire analytics events only for genuinely new additions.
 */
export function addSeatsToCart(
    product: { id: number; name: string; slug: string },
    imageUrl: string | null,
    eventId: number,
    eventLabel: string,
    seats: CartSeatItem[],
): CartSeatItem[] {
    const cart = readCart();
    const newlyAdded: CartSeatItem[] = [];
    for (const seat of seats) {
        const cartItemId = `${product.id}-event-${eventId}-seat-${seat.seat_id}`;
        if (!cart.find((i: any) => i.cartItemId === cartItemId)) {
            cart.push({
                cartItemId,
                id: product.id,
                variant_id: null,
                event_details_id: eventId,
                ticket_section: seat.section,
                seat_id: seat.seat_id,
                name: `${product.name} – bilet`,
                price: seat.price,
                image: imageUrl,
                slug: product.slug,
                quantity: 1,
                is_one_off: true,
                // Mirrors the backend's 15m cart hold — used to proactively drop the
                // line item once the hold has almost certainly expired server-side.
                held_until: Date.now() + CART_HOLD_MS,
                attributes: {
                    'Termin': eventLabel,
                    'Rząd': seat.rowLabel,
                    'Miejsce': seat.seatLabel,
                    'Typ biletu': seat.section,
                },
            });
            newlyAdded.push(seat);
        }
    }
    writeCart(cart);
    return newlyAdded;
}

export type SeatHoldResult = 'ok' | 'taken' | 'error';

/**
 * Places/extends a seat hold. 'taken' = the backend definitively refused it (someone else holds or
 * bought the seat, it was blocked, or the per-session limit was hit); 'error' = network/server
 * trouble, where the caller should not punish the shopper — checkout re-validates anyway.
 */
export async function requestSeatHold(eventId: number, seatId: string, sessionToken: string, cart = false): Promise<SeatHoldResult> {
    try {
        await fetchAPI('/seats/hold', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ event_details_id: eventId, seat_id: seatId, session_token: sessionToken, cart }),
        });
        return 'ok';
    } catch (e: any) {
        const status = Number(e?.status);
        return status >= 400 && status < 500 ? 'taken' : 'error';
    }
}

/**
 * Re-asserts this browser's hold on every seat ticket in the cart (and restarts the cart TTL).
 * Seats the backend refuses — taken by another shopper after our hold lapsed, or blocked by the
 * box office in the meantime — are dropped. Applies the outcome both to the stored cart and to the
 * caller's own `items` copy (returned), so a caller that writes its copy back later doesn't
 * restore a dropped seat or the stale held_until.
 */
export async function revalidateCartSeats(items: any[]): Promise<{ items: any[]; lost: any[] }> {
    const sessionToken = getOrCreateSeatSessionToken();
    const seatItems = items.filter((i: any) => i.seat_id && i.event_details_id && i.cartItemId);
    if (!seatItems.length || !sessionToken || sessionToken === 'ssr') return { items, lost: [] };

    const results = await Promise.all(
        seatItems.map(async (i: any) => [i.cartItemId, await requestSeatHold(i.event_details_id, i.seat_id, sessionToken, true)] as const)
    );
    const outcome = new Map(results);
    const heldUntil = Date.now() + CART_HOLD_MS;
    const apply = (list: any[]) => list
        .filter((i: any) => !(i.seat_id && outcome.get(i.cartItemId) === 'taken'))
        .map((i: any) => (i.seat_id && outcome.get(i.cartItemId) === 'ok' ? { ...i, held_until: heldUntil } : i));

    // Re-read: the stored cart may have changed while the requests were in flight.
    writeCart(apply(readCart()));
    return {
        items: apply(items),
        lost: items.filter((i: any) => i.seat_id && outcome.get(i.cartItemId) === 'taken'),
    };
}

export interface PickPackSelectionEntry {
    part_id: number;
    quantity: number;
}

/**
 * Adds a Pick-Pack composition as a single cart line item. The bundle_selection
 * payload is a lookup key for the backend to re-validate against the product's
 * pickpack_categories/parts — never a price carrier (price always comes from
 * `product.price`, exactly like any other product).
 */
export function addPickPackToCart(
    product: { id: number; name: string; slug: string; price: number },
    imageUrl: string | null,
    selection: PickPackSelectionEntry[],
    summary: string,
): void {
    const cart = readCart();
    const cartItemId = `pickpack-${product.id}-${Date.now()}`;
    cart.push({
        cartItemId,
        id: product.id,
        variant_id: null,
        name: product.name,
        price: product.price,
        image: imageUrl,
        slug: product.slug,
        quantity: 1,
        bundle_selection: selection,
        attributes: {
            'Zestaw': summary,
        },
    });
    writeCart(cart);
}

export interface CustomConfigSelectionEntry {
    attribute_id: number;
    attribute_name: string;
    value_ids: number[];
    value_labels: string[];
    price_delta: number;
}

/**
 * Adds a configurable product's (product_type === 'configurable') live-priced selection as a
 * single cart line item, mirroring addPickPackToCart above. `custom_config` is a lookup key
 * for the backend to re-validate against the product's product_attributes/attribute_conditions
 * and recompute the price fresh via compute_configurable_price — never a price carrier.
 * `computedPrice` is only ever the client-side estimate shown in the cart for UX; the price
 * actually charged is always recomputed server-side at order creation, exactly like every
 * other price shown before checkout in this app.
 */
export function addConfigurableToCart(
    product: { id: number; name: string; slug: string },
    selection: CustomConfigSelectionEntry[],
    computedPrice: number,
    quantity: number,
    imageUrl: string | null,
    /** Extra line fields, e.g. product_type / min_quantity for a D2D service with options. */
    extra: Record<string, unknown> = {},
): void {
    const cart = readCart();
    const cartItemId = `configurable-${product.id}-${Date.now()}`;
    const attributes: Record<string, string> = {};
    for (const sel of selection) {
        attributes[sel.attribute_name] = sel.value_labels.join(', ');
    }
    cart.push({
        cartItemId,
        id: product.id,
        variant_id: null,
        name: product.name,
        price: computedPrice,
        image: imageUrl,
        slug: product.slug,
        quantity,
        custom_config: selection,
        attributes,
        ...extra,
    });
    writeCart(cart);
}

/**
 * Adds a bundle product (product_type === 'bundle') as a single cart line item.
 */
export function addBundleToCart(
    product: { id: number; name: string; slug: string; price: number; sales_price?: number | null },
    bundleItems: any[],
    quantity: number = 1,
    imageUrl: string | null = null,
): void {
    const cart = readCart();
    const cartItemId = `bundle-${product.id}-${Date.now()}`;
    const activePrice = product.sales_price !== null && product.sales_price !== undefined ? product.sales_price : product.price;
    cart.push({
        cartItemId,
        id: product.id,
        variant_id: null,
        product_type: 'bundle',
        name: product.name,
        price: activePrice,
        image: imageUrl,
        slug: product.slug,
        quantity,
        bundle_items: bundleItems,
    });
    writeCart(cart);
}


/**
 * Puts a simple/variant product line in the cart unless it is already there (quantity is left
 * as the customer set it). Used by flows that continue straight to checkout with the product
 * on the page, e.g. the LeaseNow leasing button.
 */
export function ensureProductInCart(product: any, variant: any, quantity: number, imageUrl: string | null): void {
    const cart = readCart();
    const cartItemId = variant ? `${product.id}-${variant.id}` : `${product.id}`;
    if (cart.some((item: any) => item.cartItemId === cartItemId)) return;
    const source = variant || product;
    const price = source.sales_price !== null && source.sales_price !== undefined ? source.sales_price : source.price;
    cart.push({
        cartItemId,
        id: product.id,
        variant_id: variant?.id || null,
        name: product.name + (variant ? ` (${Object.values(variant.attributes || {}).join(', ')})` : ''),
        price,
        image: imageUrl,
        slug: product.slug,
        quantity: product.is_one_off ? 1 : Math.max(1, quantity || 1),
        attributes: variant?.attributes || null,
        is_one_off: !!product.is_one_off,
        product_type: product.product_type,
        requires_shipping: product.requires_shipping,
    });
    writeCart(cart);
}
